import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
  Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField as MuiTextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { fmtPhone } from '../../utils/formatters';
import { useToast } from '../../components/shared/Toast';
import { useConfirmModal } from '../../components/shared/ConfirmModal';
import brokerCompanyApi from '../../services/brokerCompanyApi';
import vestaContactsApi from '../../services/vestaContactsApi';
import StatusBadge from '../../components/shared/StatusBadge';
import SectionHeader from '../../components/shared/SectionHeader';
import PageHeader from '../../components/shared/PageHeader';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import SelectField from '../../components/form/SelectField';
import { Roles } from '../../constants';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const SYNC_STATUS_MAP = {
  matched: { variant: 'success', label: 'Synced' },
  pro_only: { variant: 'info', label: 'ProPortal Only' },
  vesta_only: { variant: 'warning', label: 'UFF Only' },
  conflict: { variant: 'danger', label: 'Conflict' },
};

const createSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().optional().default(''),
  phoneNumber: z.string().optional().default(''),
  companyName: z.string().min(1, 'Company name is required'),
  companyNmlsId: z.string().min(1, 'Company NMLS is required'),
  individualNmlsId: z.string().optional().default(''),
  branchNmlsId: z.string().optional().default(''),
  brokerRoleType: z.string().default('LoanOriginator'),
  loanChannel: z.string().default('Wholesale'),
  address: z.object({
    street: z.string().optional().default(''),
    unitNumber: z.string().optional().default(''),
    city: z.string().optional().default(''),
    state: z.string().max(2).optional().default(''),
    zipCode: z.string().optional().default(''),
  }),
});

const createDefaults = {
  firstName: '', lastName: '', emailAddress: '', phoneNumber: '',
  companyName: '', companyNmlsId: '', individualNmlsId: '', branchNmlsId: '',
  brokerRoleType: 'LoanOriginator', loanChannel: 'Wholesale',
  address: { street: '', unitNumber: '', city: '', state: '', zipCode: '' },
};

const ROLE_OPTIONS = [
  { value: 'LoanOriginator', label: 'Loan Originator' },
  { value: 'LoanProcessor', label: 'Loan Processor' },
  { value: 'LoanOfficerAssistant', label: 'Loan Officer Assistant' },
  { value: 'BranchManager', label: 'Branch Manager' },
  { value: 'CompanyAdmin', label: 'Company Admin' },
  { value: 'Other', label: 'Other' },
];

const CHANNEL_OPTIONS = [
  { value: 'Wholesale', label: 'Wholesale' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Correspondent', label: 'Correspondent' },
  { value: 'Broker', label: 'Broker' },
];

const VestaContactSyncPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedCompanyId, setSelectedCompanyId] = useState(searchParams.get('companyId') || null);
  const [activeTab, setActiveTab] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const pullCompanyRefs = useRef({});

  const { data: companies = [] } = brokerCompanyApi.endpoints.getBrokerCompanies.useQuery();
  const { data: vestaContacts = [], isLoading: vestaLoading, refetch: refetchVesta } = vestaContactsApi.endpoints.getVestaContacts.useQuery();
  const { data: syncData, isLoading: syncLoading, error: syncError, refetch: refetchSync } = vestaContactsApi.endpoints.getSyncComparison.useQuery(
    selectedCompanyId, { skip: !selectedCompanyId }
  );

  const [pushContact] = vestaContactsApi.endpoints.pushContactToVesta.useMutation();
  const [pullContact] = vestaContactsApi.endpoints.pullContactFromVesta.useMutation();
  const [pullCompany] = vestaContactsApi.endpoints.pullCompanyFromVesta.useMutation();
  const [assignRole] = vestaContactsApi.endpoints.assignUserRole.useMutation();
  const [createContact, { isLoading: creating }] = vestaContactsApi.endpoints.createVestaContact.useMutation();

  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();

  const createMethods = useForm({ resolver: zodResolver(createSchema), defaultValues: createDefaults });

  const handlePushToVesta = async (userId, brokerCompanyId) => {
    setLoadingAction(`push-${userId}`);
    try {
      await pushContact({ userId, brokerCompanyId, brokerRoleTypes: ['LoanOriginator'] }).unwrap();
      refetchSync();
      refetchVesta();
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to push contact to UFF'), 'danger');
    } finally { setLoadingAction(null); }
  };

  const handlePullFromVesta = async (vestaContactId, brokerCompanyId) => {
    setLoadingAction(`pull-${vestaContactId}`);
    try {
      const result = await pullContact({ vestaContactId, brokerCompanyId, role: Roles.Broker }).unwrap();
      showToast(`${result.action === 'created' ? 'Created' : 'Updated'} user: ${result.name}`);
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to pull contact from UFF'), 'danger');
    } finally { setLoadingAction(null); }
  };

  const handleAssignBrokerAdmin = async (userId) => {
    const confirmed = await confirm({
      title: 'Promote to Broker Admin',
      message: 'Promote this user to Broker Admin? This will replace their current role.',
      confirmText: 'Promote',
      confirmClass: 'btn-warning',
    });
    if (!confirmed) return;
    setLoadingAction(`assign-${userId}`);
    try {
      await assignRole({ userId, role: Roles.BrokerAdmin }).unwrap();
      showToast('User promoted to Broker Admin.');
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to assign role.'), 'danger');
    } finally { setLoadingAction(null); }
  };

  const handlePullCompany = async (vestaContactId) => {
    setLoadingAction(`pullco-${vestaContactId}`);
    try {
      const result = await pullCompany({ vestaContactId }).unwrap();
      if (result.action === 'exists') {
        showToast(`Company "${result.companyName}" already exists in ProPortal.`, 'info');
      } else {
        showToast(`Created company "${result.companyName}" in ProPortal.`);
      }
      setSelectedCompanyId(result.companyId);
      setActiveTab(0);
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to pull company from UFF'), 'danger');
    } finally { setLoadingAction(null); }
  };

  const handleCreateContact = async (data) => {
    try {
      await createContact({
        ...data,
        brokerRoleTypes: [data.brokerRoleType],
        loanChannels: [data.loanChannel],
      }).unwrap();
      setShowCreateModal(false);
      createMethods.reset(createDefaults);
      refetchVesta();
      if (selectedCompanyId) refetchSync();
    } catch (err) {
      showToast(getAxiosErrorMessage(err, 'Failed to create contact in UFF'), 'danger');
    }
  };

  const handleOpenCreate = () => {
    createMethods.reset(createDefaults);
    setShowCreateModal(true);
  };

  const DiffTable = ({ diffs }) => (
    <Table size="small" sx={{ '& td, & th': { fontSize: '0.75rem', py: 0.5, px: 1 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '30%' }}><strong>Field</strong></TableCell>
          <TableCell sx={{ width: '35%' }}>ProPortal</TableCell>
          <TableCell sx={{ width: '35%' }}>UFF</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {diffs.map((d, i) => (
          <TableRow key={i}>
            <TableCell><strong>{d.field}</strong></TableCell>
            <TableCell><Typography variant="caption" color="primary.main">{d.proPortalValue || <em>empty</em>}</Typography></TableCell>
            <TableCell><Typography variant="caption" color="warning.main">{d.vestaValue || <em>empty</em>}</Typography></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      {ToastContainer}
      {ConfirmModalContainer}

      <PageHeader
        title="UFF Contact Sync"
        subtitle="Manage file contacts between ProPortal and UFF LOS"
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>Create UFF Contact</Button>}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<SyncAltIcon fontSize="small" />} iconPosition="start" label="Sync Manager" />
        <Tab icon={<CloudIcon fontSize="small" />} iconPosition="start" label="All UFF Contacts" />
      </Tabs>

      {/* ── Sync Manager Tab ── */}
      {activeTab === 0 && (
        <Box>
          {/* Company Selector */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <MuiTextField
                  select size="small" label="Select Broker Company" sx={{ minWidth: 320 }}
                  value={selectedCompanyId || ''}
                  onChange={(e) => setSelectedCompanyId(e.target.value || null)}
                >
                  <MenuItem value="">-- Select a company --</MenuItem>
                  {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name} (NMLS: {c.nmls})</MenuItem>)}
                </MuiTextField>
                {selectedCompanyId && (
                  <IconButton onClick={refetchSync} disabled={syncLoading} title="Refresh"><RefreshIcon /></IconButton>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Sync Loading / Error */}
          {selectedCompanyId && syncLoading && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Comparing contacts...</Typography>
            </Box>
          )}
          {selectedCompanyId && syncError && (
            <Alert severity="error" action={<Button size="small" color="error" variant="outlined" onClick={refetchSync}>Retry</Button>} sx={{ mb: 3 }}>
              <strong>Error loading sync data:</strong> {getAxiosErrorMessage(syncError, 'Unknown error')}
            </Alert>
          )}

          {syncData && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Synced', value: syncData.matchedCount, color: 'success.main' },
                  { label: 'ProPortal Only', value: syncData.proOnlyCount, color: 'primary.main' },
                  { label: 'UFF Only', value: syncData.vestaOnlyCount, color: 'warning.main' },
                  { label: 'Conflicts', value: syncData.conflictCount, color: 'error.main' },
                ].map(({ label, value, color }) => (
                  <Grid key={label} size={{ xs: 6, md: 3 }}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Company Sync */}
              {syncData.companySync && (
                <Card sx={{ mb: 3 }}>
                  <CardHeader
                    title={<SectionHeader title={`Company Data — ${syncData.companyName}`} icon={<BusinessIcon fontSize="small" />} sx={{ mb: 0 }} />}
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" startIcon={<ArrowForwardIcon />}
                          disabled={loadingAction === `push-company-${selectedCompanyId}`}
                          onClick={async () => {
                            setLoadingAction(`push-company-${selectedCompanyId}`);
                            try {
                              const users = syncData.contacts.filter(c => c.proPortalUserId);
                              if (users.length > 0) {
                                await pushContact({ userId: users[0].proPortalUserId, brokerCompanyId: selectedCompanyId, brokerRoleTypes: ['LoanOriginator'] }).unwrap();
                              }
                              refetchSync();
                              showToast('Company data pushed to UFF via contact update.');
                            } catch (err) {
                              showToast(getAxiosErrorMessage(err, 'Failed to push company data.'), 'danger');
                            } finally { setLoadingAction(null); }
                          }}
                        >Push PRO → UFF</Button>
                        <Button size="small" variant="outlined" color="warning" startIcon={<ArrowBackIcon />}
                          disabled={loadingAction === `pull-company-${selectedCompanyId}`}
                          onClick={async () => {
                            setLoadingAction(`pull-company-${selectedCompanyId}`);
                            try {
                              const vestaContact = syncData.contacts.find(c => c.vestaContactId);
                              if (!vestaContact) { showToast('No UFF contact to pull from.', 'warning'); return; }
                              await pullContact({ vestaContactId: vestaContact.vestaContactId, brokerCompanyId: selectedCompanyId, role: Roles.Broker }).unwrap();
                              refetchSync();
                              showToast('Company + contact data pulled from UFF.');
                            } catch (err) {
                              showToast(getAxiosErrorMessage(err, 'Failed to pull company data.'), 'danger');
                            } finally { setLoadingAction(null); }
                          }}
                        >Pull UFF → PRO</Button>
                      </Stack>
                    }
                  />
                  <CardContent>
                    {syncData.companySync.diffs?.length > 0
                      ? <DiffTable diffs={syncData.companySync.diffs} />
                      : <Typography variant="body2" color="success.main"><CheckCircleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />Company data is in sync</Typography>
                    }
                  </CardContent>
                </Card>
              )}

              {/* Contact Comparison */}
              <Card>
                <CardHeader title={<SectionHeader title={`Contact Comparison — ${syncData.companyName} (NMLS: ${syncData.companyNmls})`} icon={<CompareArrowsIcon fontSize="small" />} sx={{ mb: 0 }} />} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>ProPortal</TableCell>
                        <TableCell>UFF</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syncData.contacts.length === 0 && (
                        <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}><Typography variant="body2" color="text.secondary">No contacts found for this company</Typography></TableCell></TableRow>
                      )}
                      {syncData.contacts.map((contact, idx) => (
                        <TableRow key={idx}>
                          <TableCell><StatusBadge status={contact.syncStatus} map={SYNC_STATUS_MAP} /></TableCell>
                          <TableCell>
                            {contact.proPortalName ? (
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{contact.proPortalName}</Typography>
                                <Typography variant="caption" color="text.secondary">{contact.proPortalEmail}</Typography>
                                {contact.proPortalNmls && <Typography variant="caption" color="text.secondary" display="block">NMLS: {contact.proPortalNmls}</Typography>}
                              </Box>
                            ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell>
                            {contact.vestaName ? (
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{contact.vestaName}</Typography>
                                <Typography variant="caption" color="text.secondary">{contact.vestaEmail}</Typography>
                                {contact.vestaNmls && <Typography variant="caption" color="text.secondary" display="block">NMLS: {contact.vestaNmls}</Typography>}
                                {contact.vestaRoles?.length > 0 && <Typography variant="caption" color="text.secondary" display="block">Roles: {contact.vestaRoles.join(', ')}</Typography>}
                              </Box>
                            ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell>
                            {contact.diffs?.length > 0 && <DiffTable diffs={contact.diffs} />}
                            {contact.syncStatus === 'matched' && !contact.diffs?.length && (
                              <Typography variant="caption" color="success.main"><CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />In sync</Typography>
                            )}
                            {contact.syncStatus === 'pro_only' && <Typography variant="caption" color="text.secondary">Not in UFF</Typography>}
                            {contact.syncStatus === 'vesta_only' && <Typography variant="caption" color="text.secondary">Not in ProPortal</Typography>}
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              {contact.syncStatus === 'pro_only' && contact.proPortalUserId && (
                                <>
                                  <Button size="small" variant="contained" startIcon={<CloudUploadIcon />} disabled={loadingAction === `push-${contact.proPortalUserId}`} onClick={() => handlePushToVesta(contact.proPortalUserId, selectedCompanyId)}>Push to UFF</Button>
                                  <Button size="small" variant="outlined" startIcon={<AdminPanelSettingsIcon />} disabled={loadingAction === `assign-${contact.proPortalUserId}`} onClick={() => handleAssignBrokerAdmin(contact.proPortalUserId)}>Make Admin</Button>
                                </>
                              )}
                              {contact.syncStatus === 'conflict' && (
                                <Stack direction="row" spacing={0.5}>
                                  <Button size="small" variant="outlined" startIcon={<ArrowForwardIcon />} disabled={loadingAction === `push-${contact.proPortalUserId}`} onClick={() => handlePushToVesta(contact.proPortalUserId, selectedCompanyId)}>Push PRO</Button>
                                  <Button size="small" variant="outlined" color="warning" startIcon={<ArrowBackIcon />} disabled={loadingAction === `pull-${contact.vestaContactId}`} onClick={() => handlePullFromVesta(contact.vestaContactId, selectedCompanyId)}>Pull UFF</Button>
                                </Stack>
                              )}
                              {contact.syncStatus === 'vesta_only' && contact.vestaContactId && (
                                <Button size="small" variant="outlined" color="warning" startIcon={<CloudDownloadIcon />} disabled={loadingAction === `pull-${contact.vestaContactId}`} onClick={() => handlePullFromVesta(contact.vestaContactId, selectedCompanyId)}>Pull to ProPortal</Button>
                              )}
                              {(contact.syncStatus === 'matched' || contact.syncStatus === 'conflict') && contact.proPortalUserId && (
                                <Button size="small" variant="outlined" startIcon={<AdminPanelSettingsIcon />} disabled={loadingAction === `assign-${contact.proPortalUserId}`} onClick={() => handleAssignBrokerAdmin(contact.proPortalUserId)}>Make Admin</Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          )}
        </Box>
      )}

      {/* ── All Vesta Contacts Tab ── */}
      {activeTab === 1 && (
        <Card>
          <CardHeader
            title={<SectionHeader title="All UFF Broker Contacts" icon={<CloudIcon fontSize="small" />} sx={{ mb: 0 }} />}
            action={<IconButton onClick={refetchVesta} disabled={vestaLoading} title="Refresh"><RefreshIcon /></IconButton>}
          />
          {vestaLoading ? (
            <LoadingSkeleton variant="table" rows={6} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>NMLS (Ind / Co)</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>Channels</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vestaContacts.length === 0 && (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}><Typography variant="body2" color="text.secondary">No broker contacts found in UFF</Typography></TableCell></TableRow>
                  )}
                  {vestaContacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{contact.firstName} {contact.lastName}</Typography>
                        {contact.phoneNumber && <Typography variant="caption" color="text.secondary">{fmtPhone(contact.phoneNumber)}</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2">{contact.companyName}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {contact.individualNmlsId && <>Ind: {contact.individualNmlsId}</>}
                          {contact.individualNmlsId && contact.companyNmlsId && ' / '}
                          {contact.companyNmlsId && <>Co: {contact.companyNmlsId}</>}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{contact.emailAddress}</Typography></TableCell>
                      <TableCell>
                        {contact.brokerRoleTypes?.map((role, i) => <Chip key={i} label={role} size="small" color="info" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />)}
                      </TableCell>
                      <TableCell>
                        {contact.loanChannels?.map((ch, i) => <Chip key={i} label={ch} size="small" color="success" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={contact.status === 'Active' ? 'success' : contact.status === 'Inactive' ? 'danger' : 'secondary'} solid>
                          {contact.status || 'N/A'}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MuiTextField
                              select size="small" defaultValue=""
                              sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                              inputRef={(el) => { pullCompanyRefs.current[contact.id] = el; }}
                            >
                              <MenuItem value="" disabled>Select company…</MenuItem>
                              {companies.map((co) => <MenuItem key={co.id} value={co.id}>{co.name}</MenuItem>)}
                            </MuiTextField>
                            <Tooltip title="Pull this contact as a user into the selected company">
                              <span>
                                <Button size="small" variant="outlined" color="warning" disabled={loadingAction === `pull-${contact.id}`}
                                  startIcon={<PersonAddIcon />}
                                  onClick={() => {
                                    const companyId = pullCompanyRefs.current[contact.id]?.value;
                                    if (!companyId) { showToast('Select a broker company first', 'warning'); return; }
                                    handlePullFromVesta(contact.id, companyId);
                                  }}
                                >Pull User</Button>
                              </span>
                            </Tooltip>
                          </Stack>
                          {contact.companyNmlsId && !companies.some(co => co.nmls === contact.companyNmlsId) && (
                            <Button size="small" variant="outlined" startIcon={<BusinessIcon />}
                              disabled={loadingAction === `pullco-${contact.id}`}
                              onClick={() => handlePullCompany(contact.id)}
                            >Pull Company</Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* ── Create Contact Modal ── */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create UFF File Contact</DialogTitle>
        <FormProvider methods={createMethods} onSubmit={handleCreateContact}>
          <DialogContent dividers>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Personal Information</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6 }}><TextField name="firstName" label="First Name" required size="small" /></Grid>
              <Grid size={{ xs: 6 }}><TextField name="lastName" label="Last Name" required size="small" /></Grid>
              <Grid size={{ xs: 6 }}><TextField name="emailAddress" label="Email" type="email" size="small" /></Grid>
              <Grid size={{ xs: 6 }}><TextField name="phoneNumber" label="Phone" type="tel" size="small" /></Grid>
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Company Information</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6 }}><TextField name="companyName" label="Company Name" required size="small" /></Grid>
              <Grid size={{ xs: 6 }}><TextField name="companyNmlsId" label="Company NMLS ID" required size="small" /></Grid>
              <Grid size={{ xs: 6 }}><TextField name="individualNmlsId" label="Individual NMLS ID" size="small" /></Grid>
              <Grid size={{ xs: 6 }}><TextField name="branchNmlsId" label="Branch NMLS ID" size="small" /></Grid>
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Address</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 8 }}><TextField name="address.street" label="Street" size="small" /></Grid>
              <Grid size={{ xs: 4 }}><TextField name="address.unitNumber" label="Unit #" size="small" /></Grid>
              <Grid size={{ xs: 5 }}><TextField name="address.city" label="City" size="small" /></Grid>
              <Grid size={{ xs: 4 }}><TextField name="address.state" label="State" size="small" placeholder="CA" inputProps={{ maxLength: 2 }} /></Grid>
              <Grid size={{ xs: 3 }}><TextField name="address.zipCode" label="Zip" size="small" inputProps={{ maxLength: 10 }} /></Grid>
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Role & Channel</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}><SelectField name="brokerRoleType" label="Broker Role" options={ROLE_OPTIONS} size="small" /></Grid>
              <Grid size={{ xs: 6 }}><SelectField name="loanChannel" label="Loan Channel" options={CHANNEL_OPTIONS} size="small" /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={creating} startIcon={<CloudUploadIcon />}>
              {creating ? 'Creating…' : 'Create in UFF'}
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </Box>
  );
};

export default VestaContactSyncPage;
